import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Bell, CheckCircle2, Loader2, Send, Smartphone } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";

type BrowserPermissionState = "default" | "denied" | "granted" | "unsupported";

interface PushPublicKeyResponse {
  data: {
    available: boolean;
    publicKey: string | null;
  };
}

function isPushSupported(): boolean {
  return (
    typeof window !== "undefined"
    && "Notification" in window
    && "serviceWorker" in navigator
    && "PushManager" in window
  );
}

function base64UrlToArrayBuffer(value: string): ArrayBuffer {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const binary = window.atob(base64);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0)).buffer;
}

export default function NotificationsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [permission, setPermission] = useState<BrowserPermissionState>("default");
  const [isAvailable, setIsAvailable] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionEndpoint, setSubscriptionEndpoint] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadPushState = useCallback(async () => {
    if (!isPushSupported()) {
      setPermission("unsupported");
      setIsAvailable(false);
      setIsSubscribed(false);
      setSubscriptionEndpoint(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const keyResponse = await apiFetch<PushPublicKeyResponse>("/crm/notifications/push/public-key");
      setIsAvailable(Boolean(keyResponse.data.available && keyResponse.data.publicKey));
      setPermission(Notification.permission as BrowserPermissionState);

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(Boolean(subscription));
      setSubscriptionEndpoint(subscription?.endpoint ?? null);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Failed to load push notification status";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPushState();
  }, [loadPushState]);

  const handleEnable = useCallback(async () => {
    if (!isPushSupported()) {
      setPermission("unsupported");
      return;
    }

    setIsSaving(true);
    setError(null);
    setMessage(null);

    try {
      const keyResponse = await apiFetch<PushPublicKeyResponse>("/crm/notifications/push/public-key");
      if (!keyResponse.data.available || !keyResponse.data.publicKey) {
        throw new Error("Push notifications are not configured for this workspace yet");
      }

      const requestedPermission = Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();
      setPermission(requestedPermission as BrowserPermissionState);

      if (requestedPermission !== "granted") {
        setMessage("Browser permission was not granted.");
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const subscription = existing ?? await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToArrayBuffer(keyResponse.data.publicKey),
      });

      const serialized = subscription.toJSON();
      if (!serialized.endpoint || !serialized.keys?.p256dh || !serialized.keys?.auth) {
        throw new Error("Browser push subscription is missing required keys");
      }

      await apiFetch("/crm/notifications/push/subscribe", {
        method: "POST",
        body: JSON.stringify(serialized),
      });

      setIsSubscribed(true);
      setSubscriptionEndpoint(serialized.endpoint);
      setMessage("Browser push notifications are enabled.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Failed to enable push notifications");
    } finally {
      setIsSaving(false);
    }
  }, []);

  const handleDisable = useCallback(async () => {
    if (!isPushSupported()) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setMessage(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      const endpoint = subscription?.endpoint ?? subscriptionEndpoint;

      if (subscription) {
        await subscription.unsubscribe();
      }

      if (endpoint) {
        await apiFetch("/crm/notifications/push/unsubscribe", {
          method: "DELETE",
          body: JSON.stringify({ endpoint }),
        });
      }

      setIsSubscribed(false);
      setSubscriptionEndpoint(null);
      setMessage("Browser push notifications are disabled.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Failed to disable push notifications");
    } finally {
      setIsSaving(false);
    }
  }, [subscriptionEndpoint]);

  const handleSendTest = useCallback(async () => {
    setIsSendingTest(true);
    setError(null);
    setMessage(null);

    try {
      await apiFetch("/crm/notifications/push/test", {
        method: "POST",
        body: JSON.stringify({}),
      });
      setMessage("Test notification sent.");
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) {
        setError("No active browser subscription was found. Enable notifications first.");
      } else {
        setError(cause instanceof Error ? cause.message : "Failed to send test notification");
      }
    } finally {
      setIsSendingTest(false);
    }
  }, []);

  const statusLabel = useMemo(() => {
    if (permission === "unsupported") return "Unsupported in this browser";
    if (!isAvailable) return "Server delivery not configured";
    if (permission === "denied") return "Blocked by browser permissions";
    if (isSubscribed) return "Enabled for this device";
    if (permission === "granted") return "Permission granted, not subscribed";
    return "Not enabled";
  }, [isAvailable, isSubscribed, permission]);

  return (
    <div className="max-w-3xl space-y-6">
      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-primary/10 p-3 text-primary">
            <Bell className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Browser push notifications</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Enable native notifications for this device so Stero can alert you even when the dashboard is not in the foreground.
              </p>
            </div>

            <div className="grid gap-4 rounded-xl border border-border bg-background/70 p-4 sm:grid-cols-[1fr_auto] sm:items-center">
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Status</p>
                <p className="text-sm text-muted-foreground">{isLoading ? "Checking browser and workspace support..." : statusLabel}</p>
                {subscriptionEndpoint && (
                  <p className="truncate text-xs text-muted-foreground">
                    Endpoint: {subscriptionEndpoint}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {isSubscribed ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void handleSendTest()}
                      disabled={isSendingTest || isSaving}
                      className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Send test
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDisable()}
                      disabled={isSaving}
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />}
                      Disable
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleEnable()}
                    disabled={isSaving || permission === "unsupported"}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
                    Enable notifications
                  </button>
                )}
              </div>
            </div>

            {message && (
              <div className="flex items-start gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{message}</span>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>Chrome and Edge require a secure context and a visible notification for every push delivery.</li>
              <li>If the browser says notifications are blocked, reset permission for this site and try again.</li>
              <li>The service worker already handles notification clicks and opens the linked Stero URL when present.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold text-foreground">Other notification controls</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Browser push is the only mounted settings control on this page today. Email digest and per-event operator notification preferences are still not wired to a truthful backend seam, so they are intentionally not exposed here yet.
        </p>
      </section>
    </div>
  );
}
